import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    FiUser,
    FiHash,
    FiArrowRight,
    FiUserPlus,
} from 'react-icons/fi';
import { IoMale, IoFemale } from 'react-icons/io5';
import { Stepper, useToast } from '../../theme/components';
import { PageHeader } from '../../theme/components/PageHeader';
import { PageButton } from '../../theme/components/PageButton';
import DateInput from '../Common/DateInput';
import './ChildDetails.css';

const DISTRICTS = [
    'Ampara', 'Anuradhapura', 'Badulla', 'Batticaloa', 'Colombo', 'Galle',
    'Gampaha', 'Hambantota', 'Jaffna', 'Kalutara', 'Kandy', 'Kegalle',
    'Kilinochchi', 'Kurunegala', 'Mannar', 'Matale', 'Matara', 'Monaragala',
    'Mullaitivu', 'Nuwara Eliya', 'Polonnaruwa', 'Puttalam', 'Ratnapura',
    'Trincomalee', 'Vavuniya',
];

const CHILD_REQUIRED = [
    'childNo', 'surname', 'firstName', 'gender', 'birthDate',
    'birthWeight', 'birthHeight', 'birthHospital', 'district',
    'gnDevision', 'address', 'registeredDate',
];

const ChildDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();

    const [formData, setFormData] = useState(location.state?.childData || {
        childNo: '',
        surname: '',
        firstName: '',
        gender: '',
        birthDate: '',
        birthWeight: '',
        birthHeight: '',
        birthHospital: '',
        district: '',
        gnDevision: '',
        registeredDate: '',
        address: '',
        deliveryWay: '',
        gestationalAge: '',
    });
    const [touched, setTouched] = useState({});
    const isEditMode = Boolean(location.state?.isEditMode);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };
    const handleBlur = (e) => {
        setTouched((prev) => ({ ...prev, [e.target.name]: true }));
    };

    const handleGenderSelect = (selectedGender) => {
        setFormData((prev) => ({ ...prev, gender: selectedGender }));
        setTouched((prev) => ({ ...prev, gender: true }));
    };

    const handleNext = () => {
        const missing = CHILD_REQUIRED.filter(
            (f) => !formData[f] || String(formData[f]).trim() === '',
        );
        if (missing.length) {
            const next = { ...touched };
            missing.forEach((m) => (next[m] = true));
            setTouched(next);
            toast.error('Please fill out all required fields marked with * before proceeding.');
            return;
        }
        navigate('/family-details', {
            state: {
                childData: formData,
                familyData: location.state?.familyData,
                originalChildData: location.state?.originalChildData,
                originalFamilyData: location.state?.originalFamilyData,
                isEditMode: location.state?.isEditMode,
            },
        });
    };

    const handleCancel = () => {
        if (isEditMode && location.state?.childData?.id) {
            navigate(`/profiles/${location.state.childData.id}`);
        } else {
            navigate('/dashboard');
        }
    };

    const errorFor = (field) => touched[field] && !formData[field] && 'Required';

    return (
        <div className="cr-wrapper">
            <PageHeader
                title="Add New Child"
                subtitle="Step 1 of 3 — child details"
                icon={FiUserPlus}
            />
            <div className="cr-container">

                <div className="cr-stepper-bar">
                    <Stepper steps={['Child', 'Family', 'Review']} currentStep={0} />
                </div>

                <div className="cr-form">

                    <div className="cr-form-group cr-full-width">
                        <label htmlFor="childNo">Registration Number <span className="req">*</span></label>
                        <div className={`cr-input-box ${errorFor('childNo') ? 'has-error' : ''}`}>
                            <FiHash className="cr-input-icon" />
                            <input
                                id="childNo"
                                type="text"
                                name="childNo"
                                value={formData.childNo}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="e.g. CH-0001"
                                autoComplete="off"
                            />
                        </div>
                        {errorFor('childNo') && <span className="cr-error-text">{errorFor('childNo')}</span>}
                    </div>

                    <div className="cr-form-group">
                        <label htmlFor="surname">Surname <span className="req">*</span></label>
                        <div className={`cr-input-box ${errorFor('surname') ? 'has-error' : ''}`}>
                            <FiUser className="cr-input-icon" />
                            <input
                                id="surname"
                                type="text"
                                name="surname"
                                value={formData.surname}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="e.g. Aththanayaka Mudiyanselage"
                            />
                        </div>
                        {errorFor('surname') && <span className="cr-error-text">{errorFor('surname')}</span>}
                    </div>

                    <div className="cr-form-group">
                        <label htmlFor="firstName">First Name <span className="req">*</span></label>
                        <div className={`cr-input-box ${errorFor('firstName') ? 'has-error' : ''}`}>
                            <FiUser className="cr-input-icon" />
                            <input
                                id="firstName"
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="e.g. Amani"
                            />
                        </div>
                        {errorFor('firstName') && <span className="cr-error-text">{errorFor('firstName')}</span>}
                    </div>

                    <div className="cr-form-group cr-full-width">
                        <label>Gender <span className="req">*</span></label>
                        <div className="cr-gender-options">
                            <button
                                type="button"
                                className={`cr-gender-btn ${formData.gender === 'Male' ? 'active' : ''}`}
                                onClick={() => handleGenderSelect('Male')}
                            >
                                <IoMale size={18} color="#3b82f6" />
                                <span>Male</span>
                            </button>
                            <button
                                type="button"
                                className={`cr-gender-btn ${formData.gender === 'Female' ? 'active' : ''}`}
                                onClick={() => handleGenderSelect('Female')}
                            >
                                <IoFemale size={18} color="#ec4899" />
                                <span>Female</span>
                            </button>
                        </div>
                        {errorFor('gender') && <span className="cr-error-text">Please select a gender</span>}
                    </div>

                    <div className="cr-form-group">
                        <label htmlFor="birthDate">Birth Date <span className="req">*</span></label>
                        <div className={`cr-input-box ${errorFor('birthDate') ? 'has-error' : ''}`}>
                            <DateInput
                                id="birthDate"
                                name="birthDate"
                                value={formData.birthDate || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                            />
                        </div>
                        {errorFor('birthDate') && <span className="cr-error-text">{errorFor('birthDate')}</span>}
                    </div>

                    <div className="cr-form-group">
                        <label htmlFor="registeredDate">Registered Date <span className="req">*</span></label>
                        <div className={`cr-input-box ${errorFor('registeredDate') ? 'has-error' : ''}`}>
                            <DateInput
                                id="registeredDate"
                                name="registeredDate"
                                value={formData.registeredDate || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                            />
                        </div>
                        {errorFor('registeredDate') && <span className="cr-error-text">{errorFor('registeredDate')}</span>}
                    </div>

                    <div className="cr-form-group">
                        <label htmlFor="birthWeight">Birth Weight (kg) <span className="req">*</span></label>
                        <div className={`cr-input-box ${errorFor('birthWeight') ? 'has-error' : ''}`}>
                            <input
                                id="birthWeight"
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                max="10"
                                name="birthWeight"
                                value={formData.birthWeight}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="3.25"
                            />
                            <span className="cr-input-suffix">kg</span>
                        </div>
                        {errorFor('birthWeight') && <span className="cr-error-text">{errorFor('birthWeight')}</span>}
                    </div>

                    <div className="cr-form-group">
                        <label htmlFor="birthHeight">Birth Height / Length (cm) <span className="req">*</span></label>
                        <div className={`cr-input-box ${errorFor('birthHeight') ? 'has-error' : ''}`}>
                            <input
                                id="birthHeight"
                                type="number"
                                inputMode="decimal"
                                step="0.1"
                                min="0"
                                max="100"
                                name="birthHeight"
                                value={formData.birthHeight}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="50.0"
                            />
                            <span className="cr-input-suffix">cm</span>
                        </div>
                        {errorFor('birthHeight') && <span className="cr-error-text">{errorFor('birthHeight')}</span>}
                    </div>

                    <div className="cr-form-group">
                        <label htmlFor="deliveryWay">Way of delivered <span className="req">*</span></label>
                        <div className="cr-input-box">
                            <select
                                id="deliveryWay"
                                name="deliveryWay"
                                value={formData.deliveryWay}
                                onChange={handleChange}
                            >
                                <option value="">Select Method</option>
                                {/* Option values match what the BE stores
                                    (see _DELIVERY_WAY_MAP in
                                    registration_service.py:34-38). Otherwise
                                    the stored value can't be matched on edit
                                    and the select renders blank. */}
                                <option value="Noraml">Normal</option>
                                <option value="Caesarean">Caesarean</option>
                                <option value="Vacuum">Vacuum</option>
                                <option value="Forceps">Forceps</option>
                            </select>
                        </div>
                    </div>

                    <div className="cr-form-group">
                        <label htmlFor="gestationalAge">Gestational Age at Birth <span className="req">*</span></label>
                        <div className="cr-input-box">
                            <select
                                id="gestationalAge"
                                name="gestationalAge"
                                value={formData.gestationalAge}
                                onChange={handleChange}
                            >
                                <option value="">Select Gestational Age</option>
                                {/* Option values match what the BE stores
                                    (see _GESTATIONAL_AGE_MAP in
                                    registration_service.py:39-43). */}
                                <option value="Pre-term (before 37 weeks)">Pre-term (before 37 weeks)</option>
                                <option value="Term (37–41 weeks)">Term (37–41 weeks)</option>
                                <option value="Post-term (42 weeks or more)">Post-term (42 weeks or more)</option>
                            </select>
                        </div>
                    </div>

                    <div className="cr-form-group cr-full-width">
                        <label htmlFor="birthHospital">Birth Hospital <span className="req">*</span></label>
                        <div className={`cr-input-box ${errorFor('birthHospital') ? 'has-error' : ''}`}>
                            <input
                                id="birthHospital"
                                type="text"
                                name="birthHospital"
                                value={formData.birthHospital}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="e.g. Base Hospital, Gampaha"
                            />
                        </div>
                        {errorFor('birthHospital') && <span className="cr-error-text">{errorFor('birthHospital')}</span>}
                    </div>

                    <div className="cr-form-group">
                        <label htmlFor="district">District (Residence) <span className="req">*</span></label>
                        <div className={`cr-input-box ${errorFor('district') ? 'has-error' : ''}`}>
                            <select
                                id="district"
                                name="district"
                                value={formData.district}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            >
                                <option value="" disabled>Select District</option>
                                {DISTRICTS.map((d) => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                        {errorFor('district') && <span className="cr-error-text">{errorFor('district')}</span>}
                    </div>

                    <div className="cr-form-group">
                        <label htmlFor="gnDevision">GN Devision (Residence) <span className="req">*</span></label>
                        <div className={`cr-input-box ${errorFor('gnDevision') ? 'has-error' : ''}`}>
                            <input
                                id="gnDevision"
                                type="text"
                                name="gnDevision"
                                value={formData.gnDevision}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="e.g. 123A"
                            />
                        </div>
                        {errorFor('gnDevision') && <span className="cr-error-text">{errorFor('gnDevision')}</span>}
                    </div>

                    <div className="cr-form-group cr-full-width">
                        <label htmlFor="address">Address <span className="req">*</span></label>
                        <div className={`cr-input-box cr-textarea-box ${errorFor('address') ? 'has-error' : ''}`}>
                            <textarea
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Enter full residential address"
                                rows="3"
                            />
                        </div>
                        {errorFor('address') && <span className="cr-error-text">{errorFor('address')}</span>}
                    </div>
                </div>

                <div className="cr-footer">
                    <PageButton type="button" variant="ghost" onClick={handleCancel}>
                        Cancel
                    </PageButton>
                    <PageButton type="button" variant="primary" endIcon={<FiArrowRight />} onClick={handleNext}>
                        Next: Family Info
                    </PageButton>
                </div>
            </div>
        </div>
    );
};

export default ChildDetails;
